'use client';

import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/navigation';
import { useCallback, useSyncExternalStore } from 'react';

/**
 * Every host we are willing to bounce a user back to.
 *
 * `return_to` arrives as an untrusted query param and is fed straight to
 * `window.location.href`, so without this check any site could link to
 * `/login?return_to=https://evil.example` and have the auth app deliver the
 * user there after a successful sign-in — a textbook open redirect, made more
 * attractive by the fact that the hop starts on a trusted origin.
 *
 * Relative paths ("/profile") are always allowed. Absolute URLs must be https
 * and land on the estate's own domain (or a subdomain of it); anything else is
 * discarded in favour of the default landing page.
 */
const ALLOWED_RETURN_HOST = "gaz.codes";

export function isSafeReturnTo(value: string | null | undefined): boolean {
  if (!value) return false;
  // Same-origin relative path. Reject protocol-relative ("//evil.example"),
  // which the URL parser would otherwise treat as an absolute URL.
  if (value.startsWith("/") && !value.startsWith("//")) return true;
  try {
    const url = new URL(value);
    if (url.protocol !== "https:") return false;
    return (
      url.hostname === ALLOWED_RETURN_HOST ||
      url.hostname.endsWith(`.${ALLOWED_RETURN_HOST}`)
    );
  } catch {
    return false;
  }
}

/**
 * A `return_to` remembered for the length of one sign-in visit.
 *
 * The query param only lives in the URL of the page the visitor landed on, and
 * plenty of routes between there and a session drop it: the register family,
 * verify-email, password reset and the home page's links all hardcode
 * `/profile` or `/login`. Remembering it here lets `ReturnToGuard` honour it
 * from whichever page the visitor ends up on once signed in, rather than
 * relying on every flow to thread it correctly.
 *
 * localStorage, not sessionStorage, because the email-link flows (magic link,
 * verify-email) open in a new tab. The TTL bounds how long an abandoned visit
 * can steer a later one, and the value is cleared the moment it is used.
 */
const PENDING_KEY = 'auth.pending_return_to';
const PENDING_TTL_MS = 30 * 60_000;
const PENDING_CHANGED = 'auth:pending-return-to';

export function rememberReturnTo(value: string | null | undefined, now = Date.now()): void {
  if (!isSafeReturnTo(value)) return;
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify({ url: value, at: now }));
    window.dispatchEvent(new Event(PENDING_CHANGED));
  } catch {
    // Storage blocked (private mode, site data disabled): the URL param still
    // works for flows that thread it; we just lose the safety net.
  }
}

export function readPendingReturnTo(now = Date.now()): string | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const { url, at } = JSON.parse(raw) as { url?: unknown; at?: unknown };
    if (typeof url !== 'string' || typeof at !== 'number') return null;
    if (now - at > PENDING_TTL_MS) return null;
    // Re-validate: storage is writable by anything running on this origin.
    return isSafeReturnTo(url) ? url : null;
  } catch {
    return null;
  }
}

export function clearPendingReturnTo(): void {
  try {
    localStorage.removeItem(PENDING_KEY);
    window.dispatchEvent(new Event(PENDING_CHANGED));
  } catch {
    // see rememberReturnTo
  }
}

function subscribePending(onChange: () => void) {
  window.addEventListener(PENDING_CHANGED, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(PENDING_CHANGED, onChange);
    window.removeEventListener('storage', onChange);
  };
}

/** Pending `return_to`, hydration-safe: null on the server and first render. */
function usePendingReturnTo(): string | null {
  return useSyncExternalStore(subscribePending, () => readPendingReturnTo(), () => null);
}

/**
 * Bounce-loop breaker.
 *
 * A service that rejects the token we hand it — a stage-audience mismatch, a
 * tier it does not grant — typically sends the visitor straight back here with
 * the same `return_to`, and an automatic redirect would then ping-pong forever.
 * Bouncing to the same target twice within this window means the target is not
 * accepting the session, so we stop and leave the visitor on the page.
 * sessionStorage because a loop is a single tab's round trip.
 */
const BOUNCE_KEY = 'auth.last_return_bounce';
const BOUNCE_WINDOW_MS = 20_000;

export function bouncedRecently(url: string, now = Date.now()): boolean {
  try {
    const raw = sessionStorage.getItem(BOUNCE_KEY);
    if (!raw) return false;
    const last = JSON.parse(raw) as { url?: unknown; at?: unknown };
    return last.url === url && typeof last.at === 'number' && now - last.at < BOUNCE_WINDOW_MS;
  } catch {
    return false;
  }
}

let navigating = false;

/**
 * Leave for `url`, once. Shared by the post-login handlers and the guard, so a
 * completion handler and the guard noticing the new session in the same tick
 * do not both restart the navigation.
 */
export function bounceTo(url: string): void {
  if (navigating) return;
  navigating = true;
  clearPendingReturnTo();
  const href = new URL(url, window.location.href).href;
  try {
    sessionStorage.setItem(BOUNCE_KEY, JSON.stringify({ url: href, at: Date.now() }));
  } catch {
    // Loop protection is best-effort.
  }
  window.location.href = href;
}

/**
 * Hook to manage the return_to redirect parameter across auth flows.
 *
 * - `returnTo`: the validated return_to from the current URL, falling back to
 *   the one remembered earlier in this visit; null when neither exists
 * - `redirectAfterAuth()`: redirects to return_to or falls back to /profile
 * - `buildReturnToParam()`: returns `&return_to=...` string for threading through internal links
 * - `appendReturnTo(path)`: appends return_to as a query param to an internal path
 */
export function useReturnTo() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const rawReturnTo = searchParams.get('return_to');
  const pending = usePendingReturnTo();
  // Validate once, here, so every consumer of this hook (and every login flow
  // that threads the value onward) gets the check for free.
  const returnTo = (isSafeReturnTo(rawReturnTo) ? rawReturnTo : null) ?? pending;

  /** Redirect to the return_to URL (full navigation) or fallback to /profile */
  const redirectAfterAuth = useCallback(() => {
    if (returnTo) {
      bounceTo(returnTo);
    } else {
      router.push('/profile');
    }
  }, [returnTo, router]);

  /** Returns `&return_to=<encoded>` if return_to exists, empty string otherwise */
  const buildReturnToParam = useCallback(
    (prefix: '?' | '&' = '&') => {
      if (!returnTo) return '';
      return `${prefix}return_to=${encodeURIComponent(returnTo)}`;
    },
    [returnTo]
  );

  /** Appends return_to to an internal path, e.g. `/login` → `/login?return_to=...` */
  const appendReturnTo = useCallback(
    (path: string) => {
      if (!returnTo) return path;
      const sep = path.includes('?') ? '&' : '?';
      return `${path}${sep}return_to=${encodeURIComponent(returnTo)}`;
    },
    [returnTo]
  );

  return { returnTo, redirectAfterAuth, buildReturnToParam, appendReturnTo };
}

/**
 * Access-token expiry from the non-HttpOnly `user_info` cookie.
 *
 * The auth BE writes `accessExpiresAt` alongside the display fields (see
 * `makeUserInfoCookie`); it is the only way client code can tell a live access
 * token from an expired one, since the token itself is HttpOnly. There can be
 * two `user_info` cookies (a `Domain=.gaz.codes` one beside a local one), and
 * browsers list the older first, so take the freshest — the same rule useAuth
 * applies. Returns null when absent or unparseable, which callers treat as
 * "expired".
 */
export function readAccessExpiresAt(): number | null {
  if (typeof document === 'undefined') return null;
  let freshest: number | null = null;
  for (const match of document.cookie.matchAll(/(?:^|;\s*)user_info=([^;]*)/g)) {
    try {
      const parsed = JSON.parse(decodeURIComponent(match[1] ?? ''));
      if (typeof parsed.accessExpiresAt === 'number' && parsed.accessExpiresAt > (freshest ?? -1)) {
        freshest = parsed.accessExpiresAt;
      }
    } catch {
      // skip the unparseable one
    }
  }
  return freshest;
}
